import * as React from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Checkbox } from '@lightspeed/flame/Checkbox';
import { Box, FlameTheme, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconInfo } from '@lightspeed/flame/Icon/Info';
import { IconLockClosed } from '@lightspeed/flame/Icon/LockClosed';
import { IconSearch } from '@lightspeed/flame/Icon/Search';
import { Input } from '@lightspeed/flame/Input';
import { InputGroup, InputGroupAddon } from '@lightspeed/flame/InputGroup';
import { RadioLabel } from '@lightspeed/flame/Radio';
import { Tooltip } from '@lightspeed/flame/Tooltip';

import { createReactRenderer } from 'helpers/react-renderer';
import { AnalyticsPromoModalLink } from 'root/apps/Analytics/AnalyticsPromoModalLink';
import { usePromoClicked } from 'root/apps/helpers/analyticsPromoUtils';
import { useScanInput } from 'root/scan/useScanInput';

import {
  ErrorMsg,
  MarginFilterToggleButton,
  PercentageInput,
  PercentageText,
  RadioContainer,
  SectionHeading,
  SectionTitle,
} from './AnalyticsDemandPO.style';
import { FilterSelect } from './FilterSelect';

const ScrolledAnchor = styled.a`
  position: relative;

  &:before {
    content: '';
    display: block;
    height: 54px;
    position: absolute;
  }
`;

type filterSelectOption = {
  name?: string;
  label?: string;
  value: number;
};

type filterData = {
  className: string;
  isCreatable: boolean;
  defaultValue?: filterSelectOption;
  defaultOptions: filterSelectOption[];
};

type Props = {
  hasAnalytics: boolean;
  isShowAllChecked: boolean;
  categoryData: filterData;
  brandData: filterData;
  showPromo: boolean;
};

const AddItemToPO = (e: React.SyntheticEvent) => {
  e.preventDefault();
  window.merchantos.controls.customFunctionOnClick(e.target as Control, {
    name: 'add_item_to_po_fnc_v2',
    pannel_id: 'view',
    context: 'view',
    confirm_msg: '',
    prevent_repeat: false,
    extra: null,
  });
};

enum POType {
  DemandForecast = 'DemandForecast',
  ManualReordering = 'ManualReordering',
}

const Promo = () => {
  const { hasBeenClicked } = usePromoClicked();

  if (hasBeenClicked) {
    return (
      <>An account manager will be reaching out to you by email soon to unlock this feature.</>
    );
  }

  return (
    <>
      <AnalyticsPromoModalLink>Contact your account manager</AnalyticsPromoModalLink> to unlock this
      feature.
    </>
  );
};

const AnalyticsDemandPO: React.FC<Props> = ({
  categoryData,
  isShowAllChecked,
  brandData,
  hasAnalytics,
  showPromo,
}) => {
  const [POTypeSelected, setPOTypeSelected] = React.useState<POType>();
  const [showMarginFilter, setShowMarginFilter] = React.useState(false);
  const [marginFilterError, setMarginFilterError] = React.useState<string | null>(null);
  const [isMarginFilterChecked, setIsMarginFilterChecked] = React.useState(false);
  const [preventGeneratePO, setPreventGeneratePO] = React.useState(false);
  const poByDemandForecastRadioRef = React.useRef<HTMLInputElement>(null);
  const marginFilterCheckboxRef = React.useRef<HTMLInputElement>(null);
  const marginFilterValueInputRef = React.useRef<HTMLInputElement>(null);
  const [showAllChecked, setShowAllChecked] = React.useState(isShowAllChecked);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      AddItemToPO(event);
      return false;
    }
    return true;
  };

  const addItemsInputRef = useScanInput({ onKeyDown });

  const validateMarginFilter = () => {
    const marginFilterValue = marginFilterValueInputRef.current?.value;
    if (marginFilterCheckboxRef.current?.checked) {
      if (marginFilterValue) {
        if (Number(marginFilterValue) >= 0 && /^\d*(\.\d{0,2})?$/.test(marginFilterValue)) {
          // Value is good
          setMarginFilterError(null);
          setPreventGeneratePO(false);
        } else {
          // Value is negative or a value with more than 2 decimal places
          setMarginFilterError(
            'Enter a positive value to 2 decimal places maximum or uncheck the box to remove the filter.'
          );
        }
      } else {
        // Checkbox is checked but Value is empty
        setMarginFilterError('Enter a value or uncheck the box to remove the filter.');
      }
    } else {
      // Checkbox is unchecked
      setMarginFilterError(null);
      setPreventGeneratePO(false);
    }
  };

  const validateMarginFilterCheckbox = () => {
    // Don't validate when you initially check the checkbox
    // but validate if the checkbox is checked again and the input value was left with an invalid value
    if (
      marginFilterCheckboxRef.current?.checked &&
      marginFilterValueInputRef.current?.value === ''
    ) {
      // Don't show an error but prevent the user from generating a PO
      setPreventGeneratePO(true);
    } else {
      validateMarginFilter();
    }
    setIsMarginFilterChecked(!isMarginFilterChecked);
  };

  React.useEffect(() => {
    if (addItemsInputRef.current) {
      addItemsInputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ConditionalMarginFilterErrorToolTip = ({
    wrapper,
    children,
  }: {
    wrapper: (children: JSX.Element) => JSX.Element;
    children: JSX.Element;
  }) => {
    return marginFilterError ? wrapper(children) : children;
  };

  function checkShowAll(event: React.ChangeEvent<HTMLInputElement>): void {
    window.viewFieldChanged('view');

    setShowAllChecked(event.target.checked);
  }

  return (
    <FlameTheme>
      <ScrolledAnchor id="additems" />
      <Box bg="blue-50" style={{ minWidth: '970px' }}>
        <SectionHeading>Add items</SectionHeading>
        <Flex flexDirection="column" justifyContent="space-between">
          <Box p={2} style={{ borderBottom: `1px solid gray-100` }}>
            <Flex justifyContent="space-between">
              {/* Radios */}
              <Box>
                <SectionTitle>Select how you want to add items</SectionTitle>
                <Box style={{ display: 'inline-grid', gridTemplateColumns: '1fr 1fr' }}>
                  {(showPromo || hasAnalytics) && (
                    <RadioContainer>
                      <RadioLabel
                        className="poAddRadio"
                        description={
                          <div className="poAddRadioLabel">
                            Calculate your quantity to order using demand forecasting calculations.{' '}
                            {!hasAnalytics && <Promo />}
                          </div>
                        }
                      >
                        {!hasAnalytics ? (
                          <>
                            <Tooltip
                              placement="bottom-start"
                              content="Contact your account manager to unlock this feature"
                            >
                              <input
                                type="radio"
                                disabled
                                style={{ marginRight: '0.4rem', marginTop: '2px' }}
                              />
                            </Tooltip>
                            <Box mr={1}>
                              <IconLockClosed />
                            </Box>
                          </>
                        ) : (
                          <input
                            data-test="demand-forecast-radio"
                            onClick={() => setPOTypeSelected(POType.DemandForecast)}
                            type="radio"
                            className="view_view boolean po-group-radio merged-label data_control"
                            name="po_by_demand_forecast"
                            ref={poByDemandForecastRadioRef}
                            value="true"
                            style={{ marginRight: '0.4rem', marginTop: '2px' }}
                          />
                        )}
                        <span>
                          Add items using demand forecasting{' '}
                          <span className="poAddRadioInfo">
                            <Tooltip
                              placement="bottom-start"
                              content="Calculate your quantity to order using demand forecasting calculations."
                              zIndex={1000}
                            >
                              <IconInfo
                                baseColor="none"
                                detailsColor1="#232429"
                                detailsColor2="#232429"
                                css={{ marginLeft: '6px' }}
                              />
                            </Tooltip>
                          </span>
                        </span>
                      </RadioLabel>
                    </RadioContainer>
                  )}
                  <RadioContainer>
                    <RadioLabel
                      className="poAddRadio"
                      description={
                        <div className="poAddRadioLabel">
                          Decide how much to order based on your minimum and maximum inventory
                          quantities.
                        </div>
                      }
                    >
                      <input
                        data-test="manual-forecast-radio"
                        onClick={() => setPOTypeSelected(POType.ManualReordering)}
                        type="radio"
                        defaultChecked={!hasAnalytics}
                        className="view_view boolean po-group-radio merged-label data_control"
                        name="po_by_demand_forecast"
                        value="false"
                        style={{ marginRight: '0.4rem', marginTop: '2px' }}
                      />
                      <span>
                        Add items using manual reordering
                        <span className="poAddRadioInfo">
                          <Tooltip
                            placement="bottom-start"
                            content="Decide how much to order based on your minimum and maximum inventory
                            quantities."
                            zIndex={1000}
                          >
                            <IconInfo
                              baseColor="none"
                              detailsColor1="#232429"
                              detailsColor2="#232429"
                              css={{ marginLeft: '6px' }}
                            />
                          </Tooltip>
                        </span>
                      </span>
                    </RadioLabel>
                  </RadioContainer>
                </Box>
              </Box>
              {/* Filters */}
              <Flex flexDirection="column" style={{ maxWidth: '600px' }}>
                <SectionTitle>Filters</SectionTitle>
                <Flex justifyContent="space-between">
                  <FilterSelect filterData={categoryData} type="categories" />
                  <FilterSelect filterData={brandData} type="brands" />
                  <div style={{ minWidth: '100px', textAlign: 'center' }}>
                    {poByDemandForecastRadioRef.current?.checked && (
                      <ConditionalMarginFilterErrorToolTip
                        wrapper={(children) => (
                          <Tooltip content={marginFilterError || ''}>{children}</Tooltip>
                        )}
                      >
                        <MarginFilterToggleButton
                          data-test="margin-filter-toggle-button"
                          disabled={!!marginFilterError || preventGeneratePO}
                          onClick={() => {
                            validateMarginFilter();
                            setShowMarginFilter(!showMarginFilter);
                          }}
                        >
                          {showMarginFilter ? 'Less filters' : 'More filters'}
                        </MarginFilterToggleButton>
                      </ConditionalMarginFilterErrorToolTip>
                    )}
                  </div>
                  {/*  If the user doesn't have Analytics, there's only one choice of PO so enable the button */}
                  {POTypeSelected || !hasAnalytics ? (
                    <Button
                      disabled={!!marginFilterError || preventGeneratePO}
                      type="button"
                      id="generatePOBtn"
                      className="po-add-items-button"
                      title="Auto Add All"
                      data-test="generate-button"
                      variant="secondary"
                      fill
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        window.merchantos.controls.customFunctionOnClick(e.target as Control, {
                          name: 'add_all_to_po_fnc_v2',
                          pannel_id: 'view',
                          context: 'view',
                          confirm_msg:
                            'Are you sure you want to automatically add all items below re-order point to this purchase?',
                          prevent_repeat: true,
                          extra: null,
                        });
                      }}
                    >
                      Generate PO
                    </Button>
                  ) : (
                    <Button
                      title="Select how you want to add items to purchase orders"
                      type="button"
                      data-test="disabled-generate-button"
                      variant="secondary"
                      fill
                      style={{ whiteSpace: 'nowrap', pointerEvents: 'auto' }}
                      disabled
                    >
                      Generate PO
                    </Button>
                  )}
                </Flex>
                <Box
                  mt={3}
                  style={{
                    display:
                      showMarginFilter && poByDemandForecastRadioRef.current?.checked
                        ? 'block'
                        : 'none',
                  }}
                >
                  <input
                    id="view_is_margin_filter_enabled"
                    className="view_view boolean data_control"
                    type="hidden"
                    name="is_margin_filter_enabled"
                    value={isMarginFilterChecked ? 'true' : 'false'}
                  />
                  <Checkbox
                    data-test="margin-filter-checkbox"
                    checked={isMarginFilterChecked}
                    ref={marginFilterCheckboxRef}
                    onChange={validateMarginFilterCheckbox}
                    label="Exclude discounted item"
                    description={
                      <Flex>
                        <PercentageText>Exclude any item with less than</PercentageText>
                        <Flex flexDirection="column">
                          <Box>
                            <Flex>
                              <InputGroup mx={1}>
                                <PercentageInput
                                  data-test="margin-filter-input"
                                  name="auto_add_margin_threshold"
                                  disabled={!marginFilterCheckboxRef.current?.checked}
                                  min="0"
                                  step=".01"
                                  pattern="^\d*(\.\d{0,2})?$"
                                  status={marginFilterError ? 'error' : undefined}
                                  ref={marginFilterValueInputRef}
                                  onChange={validateMarginFilter}
                                  type="number"
                                  placeholder="0"
                                  className="view_view number percentageInput data_control"
                                />
                                <InputGroupAddon>%</InputGroupAddon>
                              </InputGroup>
                              <PercentageText>margin.</PercentageText>
                            </Flex>
                          </Box>
                          {marginFilterError && (
                            <Box m={1}>
                              <ErrorMsg data-test="margin-filter-error">
                                {marginFilterError}
                              </ErrorMsg>
                            </Box>
                          )}
                        </Flex>
                      </Flex>
                    }
                  />
                </Box>
              </Flex>
            </Flex>
          </Box>
          <Divider color="disabled" />
          <Box p={2} style={{ maxWidth: '500px' }}>
            <Box mb={1}>
              <Input
                ref={addItemsInputRef}
                autoFocus
                id="view_add_item_search"
                data-test="add-item-search-input"
                name="add_item_search"
                className="view_view string itembarcode po-group-padded-input priority_auto_focus data_control"
                prefix={<IconSearch />}
                placeholder="Enter a product"
                suffix={
                  <Button
                    className="barcodesubmit"
                    type="button"
                    data-test="add-item-search-button"
                    id="add_item_to_po"
                    variant="secondary"
                    fill
                    style={{ marginRight: '-0.75rem', whiteSpace: 'nowrap' }}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      AddItemToPO(e);
                    }}
                  >
                    Add item
                  </Button>
                }
              />
            </Box>
            <Checkbox
              label="Search all catalogs"
              checked={showAllChecked}
              onChange={checkShowAll}
            />
            <input
              type="checkbox"
              style={{ display: 'none' }}
              name="add_item_show_all"
              id="view_add_item_show_all"
              checked={showAllChecked}
              className="view_view boolean po-group-checkbox merged-label data_control"
            />
          </Box>
        </Flex>
      </Box>
    </FlameTheme>
  );
};

const RenderedComponent = createReactRenderer({
  component: AnalyticsDemandPO,
  selector: '[data-react-root="AnalyticsDemandPO"]',
});

export default AnalyticsDemandPO;

document.addEventListener('DOMContentLoaded', () => {
  if (window.lsCloud.Modules.hasModule('analytics.inventory.integration')) {
    history.onChangeState(
      [
        {
          name: ['purchase.views.purchase'],
          form_name: 'view',
          id: undefined,
        },
      ],
      () => {
        RenderedComponent.mount();
      },
      () => {
        RenderedComponent.unmount();
      }
    );
  }
});
